/**
 * E2 · বিস্তার ও টাইমলাইন — `/activity/:id`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame E2 (360 × 680).
 *
 * Frame note: "Status card, timeline, costs, actions — the same four blocks for an
 * order, a booking, a payment and a request. The unknown part cost says so rather than
 * showing ৳ 0."
 *
 * The parts line reads `দেখে জানাবেন`. It is not a formatted zero and it cannot become
 * one: `ActivityCostLine`'s unknown case has no amount field at all (`@oprappo/contracts`
 * § activity), and the SQL behind it carries a check constraint saying the same
 * (`0021_account_activity.sql § activity_costs`). Showing an unknown cost as zero is the
 * easiest way to lie about what a repair will cost.
 *
 * WIRED. `GET /v1/activity/:id` supplies the header, the status card, the timeline, the
 * costs and the two footer actions — and E4's panel with them, so the support door and
 * this screen can never disagree about which order is being complained about.
 *
 * KNOWN DEFECT 1 (one instance on this frame): the status time `১১:৪০` is drawn
 * `font:500 14px/1.3 Manrope` — Bangla numerals in a family that has none. Set in Anek
 * Bangla at the drawn 14px/1.3. Only the FAMILY is substituted; the metrics are the
 * frame's, because the frame's metrics are what the screen is measured against.
 *
 * WHY THE STATUS CARD AND THE TIMELINE ARE WRITTEN OUT HERE rather than taken from
 * `@oprappo/ui`. Both components transcribe `Oprappo Design System.dc.html § 05`
 * faithfully, and § 05 is not what E2 draws. The component values are right for the
 * system and wrong for this screen, on nine counts:
 *  · Status: title 16px vs the frame's 17px · padding `13px 14px` vs a flat 14px ·
 *    progress track `#E2DED5` vs `#EDEAE1`, at `margin-top:11px` vs 12px · detail
 *    `#1A2422` (ink2) at `margin-top:8px` vs `#3A4846` (body) at 9px · time 13px/1.4
 *    vs 14px/1.3.
 *  · Timeline: `gap:12px`, `min-height:28px`, `padding-bottom:15px` and a 13px meta,
 *    against the frame's 13 / 30 / 16 / 13.5px — and the frame gives the meta a
 *    `margin-top:2px` the component has none of.
 * Those errors compound down the screen: the timeline alone lost ~4px a row.
 *
 * A component cannot be edited from here (WP-D owns `apps/web/**` only), and it should
 * not be: § 05 is its normative source and it matches it. The frame is this screen's
 * normative source. Where the two disagree, the screen follows the frame. The divergence
 * itself is real and is raised in the report as a question for the design owner, not
 * papered over in either direction.
 *
 * On one point the component is RIGHT and the doc comment that used to sit here was
 * wrong: `Timeline`'s future label resolves to `text-muted` (#5B6B69), which is exactly
 * what this frame draws. Its own docstring claims #8B9795; the code does not do that.
 * Nothing to fix, and the previous recommendation to "change the component to muted" is
 * withdrawn.
 *
 * The 11px dots that carry a BORDER (`current`, `future`) are `box-content`. The design
 * canvas sets `box-sizing:border-box` on frame boxes and nowhere else, so a drawn
 * `width:11px;border:2px` ring is 15px across; this app's reset would draw it 11px.
 * Same rule as `Radio` in offering/chrome.tsx. The filled `done` dot has no border and
 * is 11px in both models.
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, IconButton } from '@oprappo/ui';
import { formatTaka } from '@oprappo/contracts';
import type { ActivityDetail } from '@oprappo/contracts';
import { Frame, SAND_FRAME } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchActivity } from './api.js';

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function E2DetailView({
  data,
  activityRef,
}: {
  data: ActivityDetail | null;
  activityRef: string;
}): JSX.Element {
  const navigate = useNavigate();
  const ref = activityRef;
  const a = data;

  return (
    <Frame className={SAND_FRAME}>
      <div className="bg-white border-b border-border px-[16px] py-[14px] flex gap-[12px] items-center">
        <IconButton control="back" size={20} onClick={() => navigate(-1)} />
        <div className="flex-1 min-w-0">
          <div className="font-anek font-semibold text-[17px] leading-[1.75] text-ink">
            {a?.title_bn ?? ''}
          </div>
          <div className="font-manrope font-normal text-[13px] leading-[1.4] text-muted">
            {a?.ref ?? ''}
          </div>
        </div>
      </div>

      <div className="flex-1 p-[16px] oprappo-scroll">
        {a === null ? null : (
        <>
        {/* `border:1px solid #E2DED5;border-left:3px solid #E8A33D;padding:14px;margin-bottom:18px` */}
        <div className="bg-white border border-border border-l-[3px] border-l-amber p-[14px] mb-[18px]">
          <div className="flex justify-between items-baseline">
            <div className="font-anek font-semibold text-[17px] leading-[1.75] text-ink">
              {a.status.title_bn}
            </div>
            {/* Drawn `500 14px/1.3 Manrope`; Bangla numerals, so Anek Bangla. */}
            <div className="font-anek font-medium text-[14px] leading-[1.3] text-muted">
              {a.status.time_bn}
            </div>
          </div>
          <div
            className="h-[4px] bg-sand-deep mt-[12px] flex"
            role="progressbar"
            aria-valuenow={a.status.percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {/*
              INLINE, AND IT HAS TO STAY INLINE. This is an order's progress — the same
              number the `aria-valuenow` above reports. It arrives from the activity, not
              from the design file, and every value between 0 and 100 is reachable, so
              there is no finite set of classes to compile. See `apps/web/public/_headers`
              § style-src.
            */}
            <div className="bg-teal" style={{ width: `${String(a.status.percent)}%` }} />
          </div>
          <div className="font-anek font-normal text-[14px] leading-[1.75] text-body mt-[9px]">
            {a.status.detail_bn}
          </div>
        </div>

        {/* `display:flex;gap:13px` per row · connector `min-height:30px` · content `padding-bottom:16px` */}
        <div className="flex flex-col">
          {a.steps.map((step, i) => {
            const last = i === a.steps.length - 1;
            return (
              <div key={step.id} className="flex gap-[13px]">
                <div className="flex-none flex flex-col items-center">
                  <div
                    className={
                      'w-[11px] h-[11px] rounded-full ' +
                      (step.state === 'done'
                        ? 'bg-teal'
                        : step.state === 'current'
                          ? 'box-content border-2 border-amber'
                          : 'box-content border-2 border-border')
                    }
                  />
                  {last ? null : <div className="w-[1px] flex-1 bg-border min-h-[30px]" />}
                </div>
                <div className={last ? 'flex-1' : 'flex-1 pb-[16px]'}>
                  <div
                    className={
                      'font-anek text-[15px] leading-[1.75] ' +
                      (step.state === 'done'
                        ? 'font-medium text-ink'
                        : step.state === 'current'
                          ? 'font-semibold text-ink'
                          : 'font-medium text-muted')
                    }
                  >
                    {step.label_bn}
                  </div>
                  {step.meta_bn === null ? null : (
                    <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                      {step.meta_bn}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-border mt-[18px] pt-[14px]">
          {a.costs.map((c, i) => (
            <div
              key={c.label_bn}
              className={
                'py-[10px] flex justify-between items-baseline' +
                (i === 0 ? '' : ' border-t border-border')
              }
            >
              <div className="font-anek font-normal text-[14px] leading-[1.75] text-muted">
                {c.label_bn}
              </div>
              {c.known ? (
                <div className="font-manrope font-medium text-[14.5px] leading-[1.35] text-ink tabular-nums">
                  {formatTaka(c.amount_minor)}
                </div>
              ) : (
                <div className="font-anek font-medium text-[14.5px] leading-[1.75] text-body">
                  {c.text_bn}
                </div>
              )}
            </div>
          ))}
        </div>
        </>
        )}
      </div>

      <div className="bg-white border-t border-border px-[16px] py-[11px] flex gap-[10px]">
        <Button
          tone="secondary"
          size="md"
          className="flex-1 box-content p-0"
          onClick={() => navigate(`/activity/${ref}/chat`)}
        >
          {a?.chat_bn ?? ''}
        </Button>
        <Button tone="secondary" size="md" className="flex-1 box-content p-0">
          {a?.call_bn ?? ''}
        </Button>
        <div className="w-[48px] h-[48px] box-content border border-border flex items-center justify-center flex-none">
          <IconButton
            control="more"
            size={18}
            stroke="#5B6B69"
            onClick={() => navigate(`/activity/${ref}/support`)}
          />
        </div>
      </div>
    </Frame>
  );
}

export function E2Detail(): JSX.Element {
  const { id } = useParams();
  const ref = id ?? '';
  const load = useCallback((signal: AbortSignal) => fetchActivity(ref, signal), [ref]);
  const { data, error } = useResource(load, ref);

  return (
    <SurfaceStates surface="activity" error={error ?? undefined}>
      <E2DetailView data={data} activityRef={ref} />
    </SurfaceStates>
  );
}
